import React, { Suspense } from 'react'
import { useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import useMenuStore, { flattenMenuUrls } from '@/store/menuStore'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const ForbiddenPage = lazyWithRetry(() => import('@/pages/Error/ForbiddenPage'))

/**
 * All routes that are always accessible regardless of menu permissions.
 * These are structural routes, not content pages.
 */
const PUBLIC_PATHS = new Set(['/dashboard'])

/**
 * 前缀匹配的路径 → 对应需要检查权限的父路径。
 *
 * 用于无独立菜单项的页面（详情页、下钻流水页等）：它们不在菜单树里，
 * 权限复用其列表页。例：`'/campaign/tasks/': '/campaign/tasks'` 让
 * `/campaign/tasks/:id` 复用任务列表页的权限。
 */
const PREFIX_TO_PARENT: Record<string, string> = {
  // 名单详情页 /campaign/lists/:id 无独立菜单项，复用名单列表页的权限
  '/campaign/lists/': '/campaign/lists',
  '/campaign/tasks/': '/campaign/tasks',
  // 入站邮件详情页 /mail/inbound-emails/:id 无独立菜单项，复用列表页的权限
  '/mail/inbound-emails/': '/mail/inbound-emails',
}

interface PermissionGuardProps {
  children: React.ReactNode
}

const ContentSpin = (
  <div className="flex items-center justify-center w-full h-full min-h-64">
    <Spin size="large" />
  </div>
)

/**
 * Per-route permission guard.
 * Must be rendered inside RouteGuard (authenticated context) and AppLayout.
 *
 * 只判权限，不判路径是否存在 —— 「路径不存在 → 404」由 `src/router/index.tsx`
 * 里 layout children 末尾的 catch-all（`path: '*'`）独占。未注册的路径根本进不到
 * 这里，所以本组件再维护一份「已注册路径」白名单只会是第二真相源：它挡不住任何
 * 真正不存在的路径，却会在有人漏同步时把合法页面误判成 404 —— 发信账号页正是
 * 这样上线即 404：路由与后端菜单都齐备，只有白名单漏了登记。
 *
 * Logic:
 *  1. If the path is in PUBLIC_PATHS → allow
 *  2. If menus are still loading → show spinner (avoids false 403 on first load)
 *  3. If the path is not in the user's menu URLs → 403
 *  4. Otherwise → render children
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({ children }) => {
  const location = useLocation()
  const pathname = location.pathname.replace(/\/$/, '') || '/'

  const menuItems = useMenuStore((state) => state.items)
  const menuLoading = useMenuStore((state) => state.loading)

  // 1. Always-accessible paths
  if (PUBLIC_PATHS.has(pathname)) {
    return <>{children}</>
  }

  // 2. Menus not yet loaded — wait to avoid flashing 403
  if (menuLoading || menuItems.length === 0) {
    return ContentSpin
  }

  // 3. Check against the user's actual menu permissions
  // For detail pages, check the corresponding parent list page permission
  const matchedPrefix = Object.keys(PREFIX_TO_PARENT).find((p) => pathname.startsWith(p))
  const checkPath = matchedPrefix ? PREFIX_TO_PARENT[matchedPrefix] : pathname
  const allowedUrls = flattenMenuUrls(menuItems)
  if (!allowedUrls.has(checkPath)) {
    return (
      <Suspense fallback={ContentSpin}>
        <ForbiddenPage />
      </Suspense>
    )
  }

  // 4. Permitted
  return <>{children}</>
}

export default PermissionGuard
